import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ChartProps } from '../../../types';
import useTheme from '../../../hooks/useTheme';
import MetricInfoPopover, { MetricInfoDefinition } from './MetricInfoPopover';

interface ExtendedChartProps extends ChartProps {
  type?: 'bar' | 'line' | 'area' | 'pie' | 'composed';
  stacked?: boolean;
  info?: MetricInfoDefinition;
  useDualAxis?: boolean;
  xAxisHeight?: number;
  xAxisTickAngle?: number;
  xAxisInterval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';
  innerRadius?: number;
  outerRadius?: number;
  yAxisLabel?: string;
}

const COLORS = [
  '#3b82f6',
  '#f97316',
  '#10b981',
  '#f59e0b',
  '#6366f1',
  '#ec4899',
  '#8b5cf6',
  '#14b8a6',
];

const Chart: React.FC<ExtendedChartProps> = ({
  data,
  xKey,
  yKeys,
  colors = COLORS,
  title,
  height = 300,
  className,
  type = 'bar',
  stacked = false,
  info,
  useDualAxis = false,
  xAxisHeight,
  xAxisTickAngle = 0,
  xAxisInterval,
  innerRadius,
  outerRadius,
  yAxisLabel,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const gridStroke = isDark ? '#4b5563' : '#e5e7eb';
  
  const axisProps = {
    stroke: isDark ? '#6b7280' : '#9ca3af',
    tick: { fill: isDark ? '#ffffff' : '#4b5563', fontSize: 12 },
    tickLine: { stroke: isDark ? '#6b7280' : '#e5e7eb' },
  };

  const isCurrencyKey = (key: string) => {
    const rightKeys = ['revenue', 'amount', 'price', 'netpayable'];
    return rightKeys.includes(key.toLowerCase());
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);

  const formatCurrencyCompact = (value: number) => {
    if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (Math.abs(value) >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const tooltipFormatter = (value: ValueType, name: NameType): [React.ReactNode, NameType] => {
    const numericValue = Number(value);
    const label = String(name ?? '');
    if (Number.isFinite(numericValue) && isCurrencyKey(label)) {
      return [formatCurrency(numericValue), label];
    }
    return [Number.isFinite(numericValue) ? numericValue.toLocaleString('en-IN') : String(value), label];
  };

  const getYAxisId = (key: string) => {
    if (!useDualAxis) return undefined;
    return isCurrencyKey(key) ? 'right' : 'left';
  };

  const renderChart = () => {
    const yLabelConfig = yAxisLabel ? {
      value: yAxisLabel,
      angle: -90,
      position: 'insideLeft',
      offset: 0,
      fill: isDark ? '#9ca3af' : '#4b5563',
      style: { textAnchor: 'middle', fontSize: '11px', fontWeight: 600 }
    } : undefined;

    switch (type) {
      case 'bar':
        return (
          <BarChart data={data} margin={{ top: 5, right: 30, left: yAxisLabel ? 25 : 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              {...axisProps}
              angle={xAxisTickAngle}
              height={xAxisHeight}
              interval={xAxisInterval}
              textAnchor={xAxisTickAngle < 0 ? 'end' : 'middle'}
            />
            {useDualAxis ? (
              <>
                <YAxis yAxisId="left" orientation="left" {...axisProps} allowDecimals={false} label={yLabelConfig} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  {...axisProps}
                  allowDecimals={false}
                  tickFormatter={(value) => formatCurrencyCompact(Number(value))}
                />
              </>
            ) : (
              <YAxis {...axisProps} allowDecimals={false} label={yLabelConfig} />
            )}
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={isDark ? { backgroundColor: '#1f2937', border: 'none', color: '#ffffff' } : {}}
              cursor={false}
            />
            <Legend wrapperStyle={isDark ? { color: '#ffffff' } : {}} />
            {yKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                yAxisId={getYAxisId(key)}
                fill={colors[index % colors.length]}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </BarChart>
        );
      case 'line':
        return (
          <LineChart data={data} margin={{ top: 5, right: 30, left: yAxisLabel ? 25 : 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              {...axisProps}
              angle={xAxisTickAngle}
              height={xAxisHeight}
              interval={xAxisInterval}
              textAnchor={xAxisTickAngle < 0 ? 'end' : 'middle'}
            />
            {useDualAxis ? (
              <>
                <YAxis yAxisId="left" orientation="left" {...axisProps} allowDecimals={false} label={yLabelConfig} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  {...axisProps}
                  allowDecimals={false}
                  tickFormatter={(value) => formatCurrencyCompact(Number(value))}
                />
              </>
            ) : (
              <YAxis {...axisProps} allowDecimals={false} label={yLabelConfig} />
            )}
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={isDark ? { backgroundColor: '#1f2937', border: 'none', color: '#ffffff' } : {}}
            />
            <Legend wrapperStyle={isDark ? { color: '#ffffff' } : {}} />
            {yKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                yAxisId={getYAxisId(key)}
                stroke={colors[index % colors.length]}
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 1 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        );
      case 'area':
        return (
          <AreaChart data={data} margin={{ top: 5, right: 30, left: yAxisLabel ? 25 : 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              {...axisProps}
              angle={xAxisTickAngle}
              height={xAxisHeight}
              interval={xAxisInterval}
              textAnchor={xAxisTickAngle < 0 ? 'end' : 'middle'}
            />
            {useDualAxis ? (
              <>
                <YAxis yAxisId="left" orientation="left" {...axisProps} allowDecimals={false} label={yLabelConfig} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  {...axisProps}
                  allowDecimals={false}
                  tickFormatter={(value) => formatCurrencyCompact(Number(value))}
                />
              </>
            ) : (
              <YAxis {...axisProps} allowDecimals={false} label={yLabelConfig} />
            )}
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={isDark ? { backgroundColor: '#1f2937', border: 'none', color: '#ffffff' } : {}}
            />
            <Legend wrapperStyle={isDark ? { color: '#ffffff' } : {}} />
            {yKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                yAxisId={getYAxisId(key)}
                stackId={stacked ? 'stack' : index.toString()}
                fill={colors[index % colors.length]}
                stroke={colors[index % colors.length]}
              />
            ))}
          </AreaChart>
        );
      case 'pie':
        const pieData = data.map((item) => ({
          name: item[xKey],
          value: item[yKeys[0]],
        }));
        return (
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              innerRadius={innerRadius}
              outerRadius={outerRadius ?? 80}
              fill="#8884d8"
              dataKey="value"
              label={false}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[index % colors.length]}
                />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={isDark ? { 
                backgroundColor: '#1f2937', 
                border: '1px solid #374151', 
                color: '#ffffff',
                borderRadius: '8px',
                padding: '8px 12px'
              } : {}} 
              formatter={(value, name) => [`${name}: ${value}`, 'Count']}
            />
            <Legend 
              verticalAlign="top" 
              align="left"
              wrapperStyle={isDark ? { color: '#ffffff' } : {}}
              formatter={(value, entry) => `${value}: ${entry.payload?.value || 0}`}
            />
          </PieChart>
        );
      case 'composed':
        return (
          <ComposedChart data={data} margin={{ top: 5, right: 30, left: yAxisLabel ? 25 : 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              {...axisProps}
              angle={xAxisTickAngle}
              height={xAxisHeight}
              interval={xAxisInterval}
              textAnchor={xAxisTickAngle < 0 ? 'end' : 'middle'}
            />
            {useDualAxis ? (
              <>
                <YAxis yAxisId="left" orientation="left" {...axisProps} allowDecimals={false} label={yLabelConfig} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  {...axisProps}
                  allowDecimals={false}
                  tickFormatter={(value) => formatCurrencyCompact(Number(value))}
                />
              </>
            ) : (
              <YAxis {...axisProps} allowDecimals={false} label={yLabelConfig} />
            )}
            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={isDark ? { backgroundColor: '#1f2937', border: 'none', color: '#ffffff' } : {}}
              cursor={false}
            />
            <Legend wrapperStyle={isDark ? { color: '#ffffff' } : {}} />
            {yKeys.map((key, index) => {
              if (isCurrencyKey(key)) {
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    yAxisId={getYAxisId(key)}
                    stroke={colors[index % colors.length]}
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 1 }}
                    activeDot={{ r: 6 }}
                  />
                );
              }
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  yAxisId={getYAxisId(key)}
                  fill={colors[index % colors.length]}
                  stackId={stacked ? 'stack' : undefined}
                />
              );
            })}
          </ComposedChart>
        );
      default:
        return <div>Unsupported chart type</div>;
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 transition-colors duration-200 ${className}`}>
      {title && (
        <div className="mb-4 flex items-center gap-2">
          <h3 className="text-lg font-medium dark:text-white">{title}</h3>
          {info && <MetricInfoPopover title={title} info={info} />}
        </div>
      )}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>{renderChart()}</ResponsiveContainer>
      </div>
    </div>
  );
};

export default Chart;
